import { useState, useEffect } from "react";
import { getSafeRedirectPath } from "@/lib/auth/safe-redirect";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { authClient } from "@/lib/auth-client";
import { getAttributionPayload, getMetaCookies } from "@/lib/attribution";
import { trackLead as trackLeadCapi } from "@/actions/tracking/track-lead";
import { trackCompleteRegistration, trackLead } from "@/lib/facebook-pixel";
import { saveAttribution } from "@/actions/attribution/save-attribution";
import { applySignupCode } from "@/actions/signup-code/apply-signup-code";
import {
  clearPartnerTypedCodeCookie,
  writePartnerTypedCodeCookie,
} from "@/lib/partner-referral/cookie";
import { accessCodeRetryPath } from "@/lib/access-codes/retry-path";

const emailSchema = z.object({
  email: z.string().email({ message: "Por favor ingresa un email válido" }),
});

const otpSchema = z.object({
  otp: z.string().length(6, { message: "El código debe tener 6 dígitos" }),
});

type EmailForm = z.infer<typeof emailSchema>;
type OtpForm = z.infer<typeof otpSchema>;

interface UseOtpAuthOptions {
  redirectTo?: string;
  onAuthenticated?: () => Promise<void>;
  funnelEnterSlug?: string;
  instantAccount?: boolean;
  metaLeadContentName?: string;
  /**
   * When true, the email step renders an optional partner referral-code input,
   * pre-filled with `initialReferralCode`. Only used on `/registro`.
   */
  enableReferralCode?: boolean;
  initialReferralCode?: string;
}

export function useOtpAuth({
  redirectTo = "/dashboard",
  onAuthenticated,
  funnelEnterSlug,
  instantAccount = false,
  metaLeadContentName,
  enableReferralCode = false,
  initialReferralCode = "",
}: UseOtpAuthOptions = {}) {
  // Validate redirectTo is a relative path to prevent open redirect attacks
  const safeRedirectTo = getSafeRedirectPath(redirectTo);
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [referralCode, setReferralCodeState] = useState(initialReferralCode);
  const [referralCodeError, setReferralCodeError] = useState<string | null>(
    null,
  );
  const setReferralCode = (value: string) => {
    setReferralCodeError(null);
    setReferralCodeState(value);
  };
  // A code that still equals the server prefill came from the link cookie, not
  // from the user, so it must follow the cookie rules (never override an
  // existing referral) instead of the typed-code rules.
  const typedReferralCode =
    referralCode.trim().toLowerCase() ===
    initialReferralCode.trim().toLowerCase()
      ? ""
      : referralCode;
  const [countdown, setCountdown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailForm = useForm<EmailForm>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const otpForm = useForm<OtpForm>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      otp: "",
    },
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown]);

  const handleSocialSignIn = async (provider: "github" | "google") => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Social sign-in is a full redirect with no post-auth client hook, so a
      // typed code is handed to the server via a short-lived cookie that the
      // `user.create` hook reads. The link cookie is already claimed there too.
      if (enableReferralCode && typedReferralCode.trim()) {
        writePartnerTypedCodeCookie(typedReferralCode);
      }
      await authClient.signIn.social({
        provider,
        callbackURL: safeRedirectTo,
      });
    } catch (err) {
      setError("Hubo un error al iniciar sesión. Por favor intenta de nuevo.");
      console.error(err);
      setIsSubmitting(false);
    }
  };

  const completeAuthenticatedFlow = async () => {
    // Save attribution data after successful sign-in
    const attributionPayload = getAttributionPayload();
    if (attributionPayload) {
      await saveAttribution(attributionPayload);
    }
    // Apply the typed code (partner referral or access code) and any referral
    // cookies at signup. A redeemed access code already grants access, so the
    // user skips plan selection; a failed one goes to the plan page prefilled
    // so the user sees the reason instead of landing in checkout.
    let codeRedirect: string | null = null;
    if (enableReferralCode) {
      try {
        const applied = await applySignupCode({ code: typedReferralCode });
        if (applied.status === "redeemed") codeRedirect = "/onboarding";
        if (applied.status === "failed") {
          codeRedirect = accessCodeRetryPath(typedReferralCode);
        }
      } catch (codeError) {
        console.error(codeError);
        if (typedReferralCode.trim()) {
          codeRedirect = accessCodeRetryPath(typedReferralCode);
        }
      }
      clearPartnerTypedCodeCookie();
    }
    // Run post-authentication callback (e.g. record masterclass lead)
    if (onAuthenticated) {
      await onAuthenticated();
    }
    try {
      const session = await authClient.getSession();
      const user = session.data?.user;
      if (
        user &&
        Date.now() - new Date(user.createdAt).getTime() <= 10 * 60 * 1000
      ) {
        await trackCompleteRegistration(
          { status: "completed" },
          `registration_${user.id}`,
        );
      }
    } catch (trackingError) {
      console.error(
        "[FB Pixel] CompleteRegistration tracking failed:",
        trackingError,
      );
    }
    window.location.href = codeRedirect ?? safeRedirectTo;
  };

  const fireMetaLead = async (
    submittedEmail: string,
    waitForServer: boolean,
  ) => {
    if (!metaLeadContentName) return;
    try {
      const eventId = crypto.randomUUID();
      const { fbp, fbc } = getMetaCookies();
      void trackLead({ content_name: metaLeadContentName }, eventId);
      const serverTracking = trackLeadCapi({
        eventId,
        email: submittedEmail,
        contentName: metaLeadContentName,
        eventSourceUrl: window.location.href,
        fbp,
        fbc,
      });
      if (waitForServer) await serverTracking;
      else {
        void serverTracking.catch((trackingError) => {
          console.error("[CAPI] Lead tracking failed:", trackingError);
        });
      }
    } catch (trackingError) {
      console.error("[Meta] Lead tracking failed:", trackingError);
    }
  };

  const sendSignInOtp = async (submittedEmail: string) => {
    await authClient.emailOtp.sendVerificationOtp({
      email: submittedEmail,
      type: "sign-in",
    });
    void fireMetaLead(submittedEmail, false);
    setEmail(submittedEmail);
    setCountdown(60);
    setStep("otp");
  };

  const handleEmailSubmit = async (data: EmailForm) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const enterEndpoint = funnelEnterSlug
        ? "/api/auth/funnel-enter"
        : instantAccount
          ? "/api/auth/register-enter"
          : null;

      if (enterEndpoint) {
        const response = await fetch(enterEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(
            funnelEnterSlug
              ? {
                  email: data.email,
                  funnelSlug: funnelEnterSlug,
                }
              : {
                  email: data.email,
                  ...(enableReferralCode && typedReferralCode.trim()
                    ? { code: typedReferralCode.trim() }
                    : {}),
                },
          ),
        });

        if (!response.ok) {
          throw new Error("Enter failed");
        }

        const result = (await response.json()) as {
          ok?: true;
          requiresOtp?: true;
          invalidCode?: true;
        };

        if (result.invalidCode) {
          setReferralCodeError(
            "Código no válido. Los códigos de descuento se ingresan al momento del pago.",
          );
          return;
        }

        if (result.requiresOtp) {
          await sendSignInOtp(data.email);
          return;
        }

        if (result.ok) {
          setEmail(data.email);
          await fireMetaLead(data.email, true);
          await completeAuthenticatedFlow();
          return;
        }

        throw new Error("Unexpected enter response");
      }

      await sendSignInOtp(data.email);
    } catch (err) {
      setError(
        funnelEnterSlug || instantAccount
          ? "Hubo un error al continuar. Por favor intenta de nuevo."
          : "Hubo un error al enviar el código. Por favor intenta de nuevo.",
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOtpSubmit = async (data: OtpForm) => {
    try {
      setIsSubmitting(true);
      setError(null);
      const result = await authClient.signIn.emailOtp(
        {
          email,
          otp: data.otp,
        },
        {
          onSuccess: async () => {
            await completeAuthenticatedFlow();
          },
          onError: (ctx: { error: { message?: string; status?: number } }) => {
            setError("Código incorrecto. Por favor intenta de nuevo.");
            otpForm.setValue("otp", "");
            setIsSubmitting(false);
            console.error(ctx.error);
          },
        },
      );

      if (result.error) {
        setError("Código incorrecto. Por favor intenta de nuevo.");
        otpForm.setValue("otp", "");
        setIsSubmitting(false);
      }
    } catch (err) {
      setError("Código incorrecto. Por favor intenta de nuevo.");
      otpForm.setValue("otp", "");
      console.error(err);
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await authClient.emailOtp.sendVerificationOtp({
        email,
        type: "sign-in",
      });
      setCountdown(60);
    } catch (err) {
      setError(
        "Hubo un error al reenviar el código. Por favor intenta de nuevo.",
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep("email");
    otpForm.reset();
    setError(null);
  };

  return {
    step,
    email,
    referralCode,
    setReferralCode,
    referralCodeError,
    enableReferralCode,
    countdown,
    isSubmitting,
    error,
    emailForm,
    otpForm,
    handleSocialSignIn,
    handleEmailSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleBack,
  };
}
